import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import VipDropStrip from "@/components/VipDropStrip";
import Footer from "@/components/Footer";
import LcdDisplay from "@/components/LcdDisplay";
import FoundingBanner from "@/components/FoundingBanner";
import { api } from "@/lib/api";
import { ArrowRight, Shield, Activity, FileSignature, Building2, Database, BarChart3, Gauge, Info, GraduationCap, BookMarked, Award, ShieldCheck, Link2, Zap, Search, Vault, Music2, TrendingUp, DollarSign } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const AUDIENCE_IMG = "/marvin-studio.png";

const ANALYZES = [
  "Copyright protection",
  "BMI / ASCAP registration",
  "SoundExchange registration",
  "MLC registration",
  "Metadata authority profiles",
  "Producer credits",
  "Split sheet usage",
  "Catalog organization",
  "Credit monitoring",
  "Business structure",
  "Rights management",
  "Release readiness",
];

const RECEIVE = [
  { icon: BarChart3, title: "BeatCredsi Score™", body: "A single number from 0–1000 measuring how protected, organized, and industry-ready your music-creator business is." },
  { icon: Database, title: "Metadata Authority Score", body: "Where your name lives — Muso.AI, AllMusic, MusicBrainz, Discogs, Jaxsta, Genius — verified and consistent." },
  { icon: Shield, title: "Risk Rating", body: "Industry Ready, Independent Professional, Developing, or Risk. Know where you stand." },
  { icon: FileSignature, title: "Missing Items Report", body: "Every gap, ranked. No fluff — just what's broken and what to fix." },
  { icon: Activity, title: "Recommended Action Plan", body: "Numbered, prioritized next moves. Click Connect and your score climbs in real time." },
  { icon: Building2, title: "Progress Tracking", body: "Your dashboard is your roadmap. Watch the gap between you and Elite Infrastructure close." },
];

const STATUS_LEVELS = [
  { range: "0 – 399", label: "High Risk", color: "#E11D48" },
  { range: "400 – 599", label: "Developing Producer", color: "#F97316" },
  { range: "600 – 699", label: "Independent Professional", color: "#F59E0B" },
  { range: "700 – 849", label: "Industry Ready", color: "#10B981" },
  { range: "850 – 1000", label: "Elite Infrastructure", color: "#D4AF37" },
];

export default function Landing() {
  const { user } = useAuth();
  const [liveStats, setLiveStats] = useState({ passports: 0, founding: null });
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/stats/public");
        if (!alive) return;
        const s = r.data?.stats || [];
        const get = (k) => s.find((x) => x.key === k)?.value ?? 0;
        setLiveStats({
          passports: get("passports_generated"),
          founding: get("founding_remaining"),
        });
      } catch { /* fall back to 0 / null */ }
    })();
    return () => { alive = false; };
  }, []);
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar />

      {/* HERO — Image header above + content section below (no overlap) */}
      <section className="relative grain overflow-hidden bg-black" data-testid="hero-section">
        {/* Top: splash artwork as the visual header — desktop version on all viewports */}
        <div className="relative w-full" style={{ aspectRatio: "3 / 2", maxHeight: "72vh" }}>
          <img
            src="/beatcredsi-splash-desktop.png"
            alt="BeatCredsi — Infrastructure Over Hype"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          {/* Fade bottom of image into black so the wordmark blends into the content section */}
          <div className="absolute inset-x-0 bottom-0 h-[42%] bg-gradient-to-t from-black via-black/90 via-40% to-transparent pointer-events-none" />
          <div className="absolute inset-x-0 bottom-0 h-[6%] bg-black pointer-events-none" />
        </div>

        {/* Bottom: content sits on solid black BELOW the image — no overlap. */}
        <div className="relative max-w-7xl mx-auto w-full px-6 md:px-12 lg:px-20 pt-2 pb-12 lg:pb-16 -mt-10 lg:-mt-14">
          {/* Description */}
          <p
            data-testid="hero-description"
            className="text-center mx-auto max-w-4xl text-base md:text-lg lg:text-xl text-neutral-200 leading-relaxed mb-8 lg:mb-10 fade-up"
          >
            The Music Infrastructure Intelligence Platform — measuring{" "}
            <span className="text-white font-medium">registrations, royalties, metadata, publishing, credits, catalogs,</span>{" "}
            and <span className="text-white font-medium">business systems</span> on a verifiable{" "}
            <span className="text-amber-400 font-semibold">0–1000 score</span>.
          </p>

          {/* 4 large action cards — icon + label */}
          <div
            data-testid="hero-cta-strip"
            className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 max-w-6xl mx-auto fade-up"
          >
            <Link
              to={user ? "/assessment" : "/signup"}
              data-testid="hero-cta-score"
              className="group relative flex items-center gap-3 sm:gap-4 px-4 sm:px-5 py-4 sm:py-5 bg-amber-500 text-black font-semibold tracking-tight hover:brightness-110 transition-all shadow-[0_0_40px_-8px_rgba(212,175,55,0.7)] border border-amber-400/60"
            >
              <span className="shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-black/15 flex items-center justify-center">
                <Gauge size={20} className="text-black" />
              </span>
              <span className="flex-1 text-left text-sm sm:text-base leading-tight">
                Get My<br className="sm:hidden" /> BeatCredsi Score
              </span>
            </Link>

            <Link
              to="#what-is-beatcredsi"
              data-testid="hero-cta-what"
              className="group relative flex items-center gap-3 sm:gap-4 px-4 sm:px-5 py-4 sm:py-5 bg-black/60 backdrop-blur-sm border border-amber-500/40 text-white hover:border-amber-500 hover:bg-amber-500/5 transition-all"
            >
              <span className="shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-full border border-amber-500/50 flex items-center justify-center group-hover:bg-amber-500/10 transition-colors">
                <Info size={18} className="text-amber-400" />
              </span>
              <span className="flex-1 text-left text-sm sm:text-base leading-tight group-hover:text-amber-300 transition-colors">
                What Is<br className="sm:hidden" /> BeatCredsi?
              </span>
            </Link>

            <Link
              to="/courses"
              data-testid="hero-cta-courses"
              className="group relative flex items-center gap-3 sm:gap-4 px-4 sm:px-5 py-4 sm:py-5 bg-black/60 backdrop-blur-sm border border-amber-500/40 text-white hover:border-amber-500 hover:bg-amber-500/5 transition-all"
            >
              <span className="shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-full border border-amber-500/50 flex items-center justify-center group-hover:bg-amber-500/10 transition-colors">
                <GraduationCap size={18} className="text-amber-400" />
              </span>
              <span className="flex-1 text-left text-sm sm:text-base leading-tight group-hover:text-amber-300 transition-colors">
                Courses
              </span>
            </Link>

            <Link
              to={user?.canonical_username ? `/c/${user.canonical_username}` : "/signup"}
              data-testid="hero-cta-passport"
              className="group relative flex items-center gap-3 sm:gap-4 px-4 sm:px-5 py-4 sm:py-5 bg-black/60 backdrop-blur-sm border border-amber-500/40 text-white hover:border-amber-500 hover:bg-amber-500/5 transition-all"
            >
              <span className="shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-full border border-amber-500/50 flex items-center justify-center group-hover:bg-amber-500/10 transition-colors">
                <BookMarked size={18} className="text-amber-400" />
              </span>
              <span className="flex-1 text-left text-sm sm:text-base leading-tight group-hover:text-amber-300 transition-colors">
                BeatCredsi<br className="sm:hidden" /> Passport
              </span>
            </Link>
          </div>

          {/* Stats grid */}
          <div
            data-testid="hero-stats"
            className="mt-6 sm:mt-8 max-w-6xl mx-auto border border-amber-500/30 bg-black/70 backdrop-blur-md fade-up"
          >
            <div className="grid grid-cols-2 lg:grid-cols-4 divide-y lg:divide-y-0 lg:divide-x divide-amber-500/15">
              {/* Static: MAX SCORE — LCD calculator */}
              <div data-testid="stat-max-score" className="flex flex-col items-center justify-center text-center py-6 sm:py-8 px-4 group hover:bg-amber-500/[0.04] transition-colors">
                <div className="mb-3 transition-transform group-hover:scale-110 drop-shadow-[0_0_18px_rgba(212,175,55,0.4)]">
                  <Award size={28} className="text-amber-400" strokeWidth={1.5} />
                </div>
                <div className="mb-2">
                  <LcdDisplay value={1000} digits={4} size="sm" />
                </div>
                <div className="text-xs sm:text-xs font-mono-tech uppercase tracking-[0.22em] text-neutral-400 leading-tight">MAX SCORE</div>
              </div>

              {/* Static: STATUS TIERS — LCD calculator */}
              <div data-testid="stat-tiers" className="flex flex-col items-center justify-center text-center py-6 sm:py-8 px-4 group hover:bg-amber-500/[0.04] transition-colors">
                <div className="mb-3 transition-transform group-hover:scale-110 drop-shadow-[0_0_18px_rgba(212,175,55,0.4)]">
                  <BarChart3 size={28} className="text-amber-400" strokeWidth={1.5} />
                </div>
                <div className="mb-2">
                  <LcdDisplay value={5} digits={3} size="sm" />
                </div>
                <div className="text-xs sm:text-xs font-mono-tech uppercase tracking-[0.22em] text-neutral-400 leading-tight">STATUS TIERS</div>
              </div>

              {/* LIVE: BeatCredsi Passports created — LCD calculator */}
              <div data-testid="stat-passport" className="flex flex-col items-center justify-center text-center py-6 sm:py-8 px-4 group hover:bg-amber-500/[0.04] transition-colors">
                <div className="mb-3 transition-transform group-hover:scale-110 drop-shadow-[0_0_18px_rgba(212,175,55,0.4)]">
                  <BookMarked size={28} className="text-amber-400" strokeWidth={1.5} />
                </div>
                <div className="mb-2">
                  <LcdDisplay value={liveStats.passports} digits={Math.max(3, String(liveStats.passports).length)} size="sm" />
                </div>
                <div className="text-xs sm:text-xs font-mono-tech uppercase tracking-[0.22em] text-neutral-300 leading-tight">BEATCREDSI</div>
                <div className="text-xs sm:text-xs font-mono-tech uppercase tracking-[0.22em] text-neutral-400 leading-tight">PASSPORTS</div>
              </div>

              {/* LIVE: Founding cohort — stealth scarcity (no counter) */}
              <div data-testid="stat-founding" className="flex flex-col items-center justify-center text-center py-6 sm:py-8 px-4 group hover:bg-amber-500/[0.04] transition-colors">
                <div className="mb-3 transition-transform group-hover:scale-110 drop-shadow-[0_0_18px_rgba(212,175,55,0.4)]">
                  <ShieldCheck size={28} className="text-amber-400" strokeWidth={1.5} />
                </div>
                <div className="mb-2 font-display text-xl sm:text-2xl text-amber-500 leading-tight">
                  {liveStats.founding === 0 ? "Enrollment Closed" : "Enrollment Open"}
                </div>
                <div className="text-xs sm:text-xs font-mono-tech uppercase tracking-[0.22em] text-neutral-300 leading-tight">FOUNDING</div>
                <div className="text-xs sm:text-xs font-mono-tech uppercase tracking-[0.22em] text-neutral-400 leading-tight">PRODUCER COHORT</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="gold-divider" />

      {/* WHAT IS BEATCREDSI */}
      <section id="what-is-beatcredsi" className="px-6 md:px-12 lg:px-24 py-24 lg:py-32 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="text-overline mb-4">01 — What is BeatCredsi?</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight mb-6 leading-tight">
              The world&apos;s first <span className="text-amber-500">Music Creator Infrastructure Scoring System.</span>
            </h2>
            <p className="text-neutral-300 leading-relaxed mb-4">
              BeatCredsi<span className="text-amber-500">™</span> measures how protected, verified, organized, and industry-ready a music creator is — producers, artists, songwriters, composers, engineers, and labels.
            </p>
            <p className="text-neutral-400 leading-relaxed mb-6">
              Just as a credit score measures financial health, BeatCredsi measures music-creator infrastructure health. One number. Every gap. Every next move.
            </p>
            <p className="text-sm text-neutral-500 italic">
              The goal: help every music creator build a professional, ownership-verified business — not just make music.
            </p>
          </div>

          <div className="lg:col-span-7">
            <div className="text-overline mb-5">The platform analyzes</div>
            <div className="grid grid-cols-2 gap-px bg-white/5">
              {ANALYZES.map((a) => (
                <div key={a} className="bg-[#0A0A0A] px-5 py-4 flex items-center gap-3">
                  <span className="w-1 h-1 bg-amber-500" />
                  <span className="text-sm text-neutral-200">{a}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-20">
          <div className="text-overline mb-6">Users receive</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 border border-white/5">
            {RECEIVE.map((d) => (
              <div key={d.title} className="bg-[#0A0A0A] p-8 hover:bg-[#121212] transition-colors group">
                <d.icon size={22} className="text-amber-500 mb-6" strokeWidth={1.5} />
                <h3 className="font-display text-xl text-white mb-3">{d.title}</h3>
                <p className="text-sm text-neutral-400 leading-relaxed">{d.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VIP Drop Live Counter Strip (public) */}
      <VipDropStrip />

      {/* SMART AUDIT LINK — 3-SECOND AUDIT + REVENUE REPORT MOCKUP */}
      <section id="smart-audit" className="px-6 md:px-12 lg:px-24 py-24 lg:py-32 border-y border-white/5 bg-[#0c0c0c]" data-testid="section-smart-audit">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-5">
              <div className="text-overline mb-4 flex items-center gap-2">
                <Zap size={12} className="text-amber-500" />
                02 — The 3-Second Audit
              </div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight mb-6 leading-tight">
                Paste one link.<br />
                <span className="text-amber-500">Find your missing money.</span>
              </h2>
              <p className="text-neutral-300 leading-relaxed mb-4">
                Drop a MusicBrainz, Muso.AI, AllMusic, or Genius URL into RoyAI — or upload a screenshot of your Spotify-for-Artists / Muso.AI chart. In <span className="text-amber-400">under three seconds</span> BeatCredsi extracts every credit, every ISRC, every stream, and every payout you haven&apos;t claimed.
              </p>
              <p className="text-neutral-400 leading-relaxed mb-8">
                No estimates. No questionnaires. Just <span className="text-white font-medium">forensic accounting</span> for your catalog, powered by the Universal Metadata Importer.
              </p>
              <div className="flex flex-wrap gap-2 mb-8">
                {["MusicBrainz", "Muso.AI", "AllMusic", "Genius", "Screenshot OCR"].map((s) => (
                  <span key={s} className="text-[10px] font-mono-tech uppercase tracking-widest text-neutral-400 border border-white/10 bg-black/60 px-2.5 py-1.5">{s}</span>
                ))}
              </div>
              <Link
                to={user ? "/royai" : "/signup"}
                data-testid="cta-smart-audit"
                className="inline-flex items-center gap-2 bg-amber-500 text-black px-6 py-3 text-sm font-mono-tech uppercase tracking-[0.2em] font-semibold hover:brightness-110 transition-all"
              >
                Run Your Audit <ArrowRight size={16} />
              </Link>
            </div>

            {/* Visual Revenue Report mockup — platform-by-platform */}
            <div className="lg:col-span-7">
              <div className="border border-amber-500/25 bg-gradient-to-b from-amber-500/[0.05] to-black p-5 lg:p-7 shadow-[0_0_120px_-40px_rgba(212,175,55,0.35)]" data-testid="revenue-report-preview">
                <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <Music2 size={14} className="text-amber-400" />
                    <span className="text-[10px] font-mono-tech uppercase tracking-widest text-neutral-500">Deep Dive · Live Preview</span>
                  </div>
                  <span className="text-[10px] font-mono-tech uppercase tracking-widest text-emerald-400 flex items-center gap-1.5">
                    <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" /><span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" /></span>
                    OCR verified
                  </span>
                </div>
                <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
                  <div>
                    <div className="font-display text-2xl text-white">Runaway (feat. Pusha T)</div>
                    <div className="text-sm text-neutral-400">Kanye West · ISRC USUM71034428 · Live since 2010</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] font-mono-tech uppercase tracking-widest text-neutral-500">Your Share (25%)</div>
                    <div className="font-display text-3xl text-amber-400">$23,925</div>
                    <div className="text-[10px] text-neutral-500">of $95,700 total</div>
                  </div>
                </div>

                <div className="space-y-1">
                  {[
                    { p: "Spotify",       s: "31.9M", r: "$95,700",  y: "$23,925", w: "90%", c: "#1DB954" },
                    { p: "Apple Music",   s: "12.3M", r: "$68,880",  y: "$17,220", w: "72%", c: "#FA57C1" },
                    { p: "YouTube Music", s: "22.5M", r: "$15,525",  y: "$3,881",  w: "48%", c: "#FF0000" },
                    { p: "Amazon Music",  s: "6.4M",  r: "$25,728",  y: "$6,432",  w: "36%", c: "#FF9900" },
                    { p: "Tidal",         s: "1.8M",  r: "$23,112",  y: "$5,778",  w: "24%", c: "#00FFFF" },
                  ].map((row) => (
                    <div key={row.p} className="grid grid-cols-12 gap-3 items-center py-2.5 border-b border-white/5">
                      <div className="col-span-3 flex items-center gap-2 min-w-0">
                        <span className="w-2 h-2 rounded-full shrink-0" style={{ background: row.c }} />
                        <span className="text-sm text-white truncate">{row.p}</span>
                      </div>
                      <div className="col-span-2 text-right font-mono-tech text-xs text-neutral-300">{row.s}</div>
                      <div className="col-span-4">
                        <div className="h-1.5 bg-white/5 relative overflow-hidden">
                          <div className="absolute inset-y-0 left-0" style={{ width: row.w, background: `linear-gradient(90deg, ${row.c}80, ${row.c})` }} />
                        </div>
                      </div>
                      <div className="col-span-2 text-right font-mono-tech text-xs text-neutral-400">{row.r}</div>
                      <div className="col-span-1 text-right font-mono-tech text-xs text-amber-400">{row.y}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-xs text-amber-300">
                    <TrendingUp size={14} /> $57,236 unclaimed across 5 platforms
                  </div>
                  <span className="text-[10px] font-mono-tech uppercase tracking-widest text-neutral-500">
                    Illustrative · your numbers will vary
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ROYAI — ROYALTY HUNTER + FORENSIC EVIDENCE VAULT */}
      <section id="royai" className="px-6 md:px-12 lg:px-24 py-24 lg:py-32 max-w-7xl" data-testid="section-royai">
        <div className="text-overline mb-6">03 — RoyAI · Royalty Intelligence</div>

        {/* Cinematic gold-glow video — muted autoplay loop */}
        <div className="relative mb-14" data-testid="royai-intro-video-wrap">
          {/* Ambient gold-glow halo */}
          <div
            className="absolute -inset-3 sm:-inset-4 pointer-events-none blur-2xl opacity-70"
            style={{
              background:
                "radial-gradient(60% 60% at 50% 40%, rgba(212,175,55,0.55) 0%, rgba(212,175,55,0.18) 45%, transparent 75%)",
            }}
            aria-hidden="true"
          />
          <div className="relative border border-amber-500/40 shadow-[0_0_60px_-10px_rgba(212,175,55,0.55)] bg-black overflow-hidden">
            <div className="relative w-full" style={{ aspectRatio: "16 / 9" }}>
              <video
                data-testid="royai-intro-video"
                src="https://customer-assets-39nsmqrw.emergentagent.net/job_producer-health/artifacts/ta5z6wo9_BEATCREDSI%20-%20INTRO%20TO%20ROYAI.mp4"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                poster=""
                className="absolute inset-0 w-full h-full object-cover"
              />
              {/* Corner brackets for luxury framing */}
              <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-amber-500 pointer-events-none" />
              <div className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-amber-500 pointer-events-none" />
              <div className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-amber-500 pointer-events-none" />
              <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-amber-500 pointer-events-none" />
              {/* Bottom-left caption chip */}
              <div className="absolute bottom-3 left-3 flex items-center gap-2 bg-black/70 backdrop-blur-sm border border-amber-500/40 px-2.5 py-1">
                <span className="w-1.5 h-1.5 bg-amber-500 animate-pulse rounded-full" />
                <span className="text-[10px] font-mono-tech uppercase tracking-widest text-amber-400">
                  RoyAI · Intro Reel
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 mb-14">
          <div className="lg:col-span-6">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight mb-6 leading-tight">
              We don&apos;t just find<br />
              your music.<br />
              <span className="text-amber-500">We find your money.</span>
            </h2>
            <p className="text-neutral-300 leading-relaxed mb-4">
              RoyAI opens the <span className="text-white font-medium">Black Box</span> of the music industry — the invisible layer where distributors hold statements, PROs miss registrations, and platforms pay the wrong wallets. The Royalty Hunter surfaces every unclaimed dollar as a <span className="text-amber-400">track-specific Bounty</span>: platform, dollar amount, and the exact next move to claim it.
            </p>
            <p className="text-neutral-400 leading-relaxed">
              Every claim is backed by the Forensic Evidence Vault™ — encrypted uploads of distributor statements, PRO registrations, split sheets, and licensing paperwork. Approved evidence instantly resolves matching bounties and raises your BeatCredsi Score in real time.
            </p>
          </div>
          <div className="lg:col-span-6 grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/5 border border-white/5">
            <div className="bg-[#0A0A0A] p-6 hover:bg-[#121212] transition-colors">
              <Search size={20} className="text-amber-400 mb-4" strokeWidth={1.5} />
              <div className="text-xs font-mono-tech uppercase tracking-widest text-neutral-500 mb-2">Royalty Hunter</div>
              <h3 className="font-display text-lg text-white mb-2">Track-Specific Bounties</h3>
              <p className="text-sm text-neutral-400 leading-relaxed">One bounty per unclaimed platform, per audited track. Estimated recovery in dollars, ranked by opportunity size.</p>
            </div>
            <div className="bg-[#0A0A0A] p-6 hover:bg-[#121212] transition-colors">
              <Vault size={20} className="text-amber-400 mb-4" strokeWidth={1.5} />
              <div className="text-xs font-mono-tech uppercase tracking-widest text-neutral-500 mb-2">Forensic Evidence Vault™</div>
              <h3 className="font-display text-lg text-white mb-2">Proof Over Promises</h3>
              <p className="text-sm text-neutral-400 leading-relaxed">Encrypted uploads of distributor PDFs, PRO records, split sheets. Verified evidence auto-resolves bounties and lifts your score.</p>
            </div>
            <div className="bg-[#0A0A0A] p-6 hover:bg-[#121212] transition-colors">
              <Link2 size={20} className="text-amber-400 mb-4" strokeWidth={1.5} />
              <div className="text-xs font-mono-tech uppercase tracking-widest text-neutral-500 mb-2">Universal Importer</div>
              <h3 className="font-display text-lg text-white mb-2">One Link, Full Audit</h3>
              <p className="text-sm text-neutral-400 leading-relaxed">MusicBrainz, Muso.AI, AllMusic, Genius, or a screenshot. RoyAI extracts, cross-checks, and quantifies.</p>
            </div>
            <div className="bg-[#0A0A0A] p-6 hover:bg-[#121212] transition-colors">
              <DollarSign size={20} className="text-amber-400 mb-4" strokeWidth={1.5} />
              <div className="text-xs font-mono-tech uppercase tracking-widest text-neutral-500 mb-2">Live Recoverable</div>
              <h3 className="font-display text-lg text-white mb-2">Real Numbers, Real Time</h3>
              <p className="text-sm text-neutral-400 leading-relaxed">Your Total Recoverable counter updates the moment evidence lands. No page refresh — SSE-pushed straight to your dashboard.</p>
            </div>
          </div>
        </div>

        {/* THE BLACK BOX — Bounty ticker with LCD dollar readouts */}
        <div className="border border-amber-500/20 bg-black/60 p-6 lg:p-8 mb-8" data-testid="black-box-bounties">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
            <div className="flex items-center gap-3">
              <span className="w-3 h-3 rounded-full bg-amber-500" style={{ boxShadow: "0 0 14px #F5C84299" }} />
              <div>
                <div className="text-overline text-amber-500">The Black Box</div>
                <h3 className="font-display text-xl lg:text-2xl text-white leading-tight">Sample bounties surfaced by RoyAI</h3>
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] font-mono-tech uppercase tracking-widest text-neutral-500">Total Recoverable · illustrative</div>
              <div className="flex items-center justify-end gap-2 mt-1">
                <span className="text-amber-400 font-mono-tech text-xl leading-none">$</span>
                <LcdDisplay value={57236} digits={5} size="md" />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/5 border border-white/5">
            {[
              { platform: "Spotify",       track: "Runaway (feat. Pusha T)",       streams: "31.9M", amount: 23925, color: "#1DB954" },
              { platform: "YouTube Music", track: "Devil in a New Dress",          streams: "22.5M", amount: 3881,  color: "#FF0000" },
              { platform: "Apple Music",   track: "Power (feat. Dwele)",           streams: "12.3M", amount: 17220, color: "#FA57C1" },
              { platform: "Tidal",         track: "Lost in the World (feat. Bon Iver)", streams: "1.8M",  amount: 5778,  color: "#22CCCC" },
            ].map((b) => (
              <div key={b.platform} className="bg-[#0A0A0A] p-5 flex items-center gap-4">
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: b.color, boxShadow: `0 0 10px ${b.color}99` }} />
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] font-mono-tech uppercase tracking-widest text-neutral-500">{b.platform} · Unclaimed · {b.streams} streams</div>
                  <div className="text-sm text-white truncate">{b.track}</div>
                </div>
                <div className="text-right shrink-0 flex items-center gap-1.5">
                  <span className="text-amber-400 font-mono-tech text-sm leading-none">$</span>
                  <LcdDisplay value={b.amount} digits={5} size="sm" />
                </div>
              </div>
            ))}
          </div>
          <div className="text-[10px] font-mono-tech uppercase tracking-widest text-neutral-500 mt-4">
            Illustrative preview · your Bounty Wall will reflect your own catalog after your first Smart Audit
          </div>
        </div>

        <div className="border-l-2 border-amber-500 bg-[#0A0A0A] px-6 lg:px-10 py-8 lg:py-10" data-testid="ioh-banner">
          <div className="text-overline mb-3">Infrastructure Over Hype™</div>
          <h3 className="font-display text-2xl lg:text-3xl font-medium mb-4 max-w-4xl leading-tight">
            While the industry sells followers, plays, and playlist placements — we sell <span className="text-amber-500">provable, verified ownership</span> of the money your music has already made.
          </h3>
          <div className="text-sm text-neutral-500 font-mono-tech uppercase tracking-widest">
            Forensic accounting for creators · Powered by RoyAI
          </div>
        </div>
      </section>

      {/* PASSPORT & VAULT — AI-FORENSIC VERIFICATION */}
      <section id="passport-trust" className="px-6 md:px-12 lg:px-24 py-24 lg:py-32 border-y border-white/5 bg-[#0c0c0c]" data-testid="section-passport-trust">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            <div className="lg:col-span-5">
              <div className="text-overline mb-4">04 — The Industry Standard</div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight mb-6 leading-tight">
                The BeatCredsi Passport™,<br />
                verified by <span className="text-amber-500">AI-Forensic Proof.</span>
              </h2>
              <p className="text-neutral-300 leading-relaxed mb-4">
                Your public Passport isn&apos;t just a profile — it&apos;s a verified credential the industry can trust. Every score, every credit, every dollar recovered is anchored to real documentation held in the Forensic Evidence Vault.
              </p>
              <p className="text-neutral-400 leading-relaxed mb-6">
                We layer two verification systems on top of the score so a BeatCredsi number carries the same weight as an audited financial statement:
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 border border-amber-500/40 bg-amber-500/10 flex items-center justify-center shrink-0">
                    <Zap size={18} className="text-amber-400" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-white mb-1">AI-Forensic OCR</div>
                    <div className="text-sm text-neutral-400 leading-relaxed">Gemini vision reads every uploaded distributor statement, split sheet, and analytics screenshot — extracting ISRCs, streams, and payouts that get cross-checked against your Vault before your score moves.</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 border border-amber-500/40 bg-amber-500/10 flex items-center justify-center shrink-0">
                    <ShieldCheck size={18} className="text-amber-400" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-white mb-1">Industry 2FA</div>
                    <div className="text-sm text-neutral-400 leading-relaxed">Every verified claim carries a second-factor confirmation from an industry source of truth — MusicBrainz, PRO databases, distributor APIs, or admin review. No one-sided attestations.</div>
                  </div>
                </div>
              </div>
              <Link
                to={user ? "/passport" : "/signup"}
                data-testid="cta-passport-trust"
                className="mt-8 inline-flex items-center gap-2 border border-amber-500/40 text-amber-400 hover:bg-amber-500/10 px-5 py-3 text-xs font-mono-tech uppercase tracking-[0.2em] font-semibold transition-all"
              >
                See a sample Passport <ArrowRight size={14} />
              </Link>
            </div>

            {/* Passport / Vault LCD display */}
            <div className="lg:col-span-7">
              <div className="border border-amber-500/25 bg-gradient-to-b from-amber-500/[0.04] to-black p-6 lg:p-8 shadow-[0_0_120px_-40px_rgba(212,175,55,0.35)]" data-testid="passport-preview-card">
                <div className="flex items-center justify-between flex-wrap gap-2 mb-6 pb-6 border-b border-white/5">
                  <div>
                    <div className="text-[10px] font-mono-tech uppercase tracking-widest text-neutral-500">BeatCredsi Passport™ · Preview</div>
                    <div className="font-display text-xl text-white">The MarTian · Producer / Songwriter</div>
                  </div>
                  <span className="inline-flex items-center gap-1.5 text-[10px] font-mono-tech uppercase tracking-widest text-emerald-400 border border-emerald-500/30 bg-emerald-500/10 px-2 py-1">
                    <ShieldCheck size={11} /> AI-Forensic Verified
                  </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/5 border border-white/5 mb-6">
                  <div className="bg-[#0A0A0A] px-4 py-5 flex flex-col items-center text-center">
                    <div className="text-[10px] font-mono-tech uppercase tracking-widest text-neutral-500 mb-2">BeatCredsi Score</div>
                    <LcdDisplay value={847} digits={3} size="lg" />
                    <div className="text-[10px] text-neutral-600 mt-1">/ 1000</div>
                  </div>
                  <div className="bg-[#0A0A0A] px-4 py-5 flex flex-col items-center text-center">
                    <div className="text-[10px] font-mono-tech uppercase tracking-widest text-neutral-500 mb-2">Verified Credits</div>
                    <LcdDisplay value={142} digits={3} size="lg" />
                    <div className="text-[10px] text-neutral-600 mt-1">2FA · confirmed</div>
                  </div>
                  <div className="bg-[#0A0A0A] px-4 py-5 flex flex-col items-center text-center">
                    <div className="text-[10px] font-mono-tech uppercase tracking-widest text-neutral-500 mb-2">Vault Docs</div>
                    <LcdDisplay value={38} digits={3} size="lg" />
                    <div className="text-[10px] text-neutral-600 mt-1">OCR · verified</div>
                  </div>
                  <div className="bg-[#0A0A0A] px-4 py-5 flex flex-col items-center text-center">
                    <div className="text-[10px] font-mono-tech uppercase tracking-widest text-neutral-500 mb-2">Recovered</div>
                    <div className="flex items-baseline gap-0.5">
                      <span className="text-amber-400 font-mono-tech text-sm leading-none">$</span>
                      <LcdDisplay value={41320} digits={5} size="lg" />
                    </div>
                    <div className="text-[10px] text-neutral-600 mt-1">lifetime · USD</div>
                  </div>
                </div>
                <div className="text-[10px] font-mono-tech uppercase tracking-widest text-neutral-500 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-emerald-500 animate-pulse" />
                  Trusted by publishers · labels · sync supervisors · legal teams
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATUS LEVELS */}
      <section className="px-6 md:px-12 lg:px-24 py-24 lg:py-32 border-y border-white/5 bg-[#0c0c0c]">
        <div className="max-w-6xl mx-auto">
          <div className="text-overline mb-6 text-center">05 — Music Creator Status Levels</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight mb-12 text-center">
            From <span style={{ color: "#E11D48" }}>High Risk</span> to <span className="text-amber-500">Elite Infrastructure.</span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-px bg-white/5">
            {STATUS_LEVELS.map((s) => (
              <div key={s.label} className="bg-[#0A0A0A] p-6 flex flex-col">
                <div className="font-mono-tech text-xs text-neutral-500 mb-3">{s.range}</div>
                <div className="font-display text-lg text-white mb-4 leading-tight">{s.label}</div>
                <div className="h-1 w-full mt-auto" style={{ background: s.color }} />
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to={user ? "/assessment" : "/signup"} data-testid="status-cta"
              className="inline-flex items-center gap-3 px-8 py-4 bg-amber-500 text-black font-semibold hover:brightness-110 transition-all">
              Discover Your BeatCredsi Score <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* WHO IT HELPS */}
      <section className="px-6 md:px-12 lg:px-24 py-24 lg:py-32 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-5 relative aspect-[4/5] overflow-hidden">
            <img src={AUDIENCE_IMG} alt="Music creator in studio" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-tr from-[#0A0A0A] via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6">
              <div className="text-overline">06 — Built for</div>
            </div>
          </div>
          <div className="lg:col-span-7">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight mb-10">
              If a music creator, lawyer, business manager, publisher, and record exec all used the same software.
            </h2>
            <ul className="space-y-5">
              {[
                "Producers releasing on streaming services",
                "Producer-engineers selling beats and licensing tracks",
                "Artists, songwriters, and composers publishing their own catalog",
                "Independent artists who also produce, write, or engineer",
                "Studios and labels building creator rosters",
                "Music creators preparing for a major release",
              ].map((h) => (
                <li key={h} className="flex items-start gap-4 group">
                  <span className="w-1 h-1 bg-amber-500 mt-3 shrink-0" />
                  <span className="text-base lg:text-lg text-neutral-200 group-hover:text-white transition-colors">{h}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
